import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  TrendingDown,
  AlertTriangle,
  Percent,
  Clock,
  ArrowRight,
  Users,
  Activity,
  Brain,
  Flame,
} from 'lucide-react';

const WINDOW_OPTIONS = [
  { value: 30,  label: '30d' },
  { value: 90,  label: '90d' },
  { value: 180, label: '180d' },
  { value: 365, label: '1y' },
];

const fmtSAR = (n) => `${(Number(n) || 0).toLocaleString()} SAR`;
const fmtPct = (n) => `${((Number(n) || 0) * 100).toFixed(1)}%`;

// ─── Funnel block ───────────────────────────────────────────────
const FunnelBlock = ({ funnel }) => {
  if (!funnel) return null;
  const maxCount = Math.max(...funnel.stages.map((s) => s.count), 1);
  const headlineRate = fmtPct(funnel.overall_conversion_rate);

  return (
    <div
      data-testid="qa-funnel-block"
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Conversion Funnel</h3>
          <p className="text-sm text-gray-500">
            Draft → Sent → Accepted → Converted · last {funnel.window_days}d
          </p>
        </div>
        <div className="text-right">
          <div
            data-testid="qa-funnel-overall-rate"
            className="text-3xl font-bold text-indigo-600"
          >
            {headlineRate}
          </div>
          <div className="text-xs text-gray-500">overall conversion</div>
        </div>
      </div>

      <div className="space-y-3">
        {funnel.stages.map((s, i) => {
          const width = `${Math.max((s.count / maxCount) * 100, 4)}%`;
          const colors = ['bg-gray-400', 'bg-blue-500', 'bg-emerald-500', 'bg-indigo-600'];
          return (
            <div key={s.stage} data-testid={`qa-funnel-stage-${s.stage}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium capitalize text-gray-700">
                  {s.stage}
                  {i > 0 && (
                    <span className="ml-2 text-xs text-gray-400">
                      ({fmtPct(s.conversion_rate_from_prev)} from {funnel.stages[i - 1].stage})
                    </span>
                  )}
                </span>
                <span className="font-mono text-gray-900">
                  {s.count} · {fmtSAR(s.amount)}
                </span>
              </div>
              <div className="h-7 bg-gray-100 rounded-md overflow-hidden">
                <div
                  className={`${colors[i]} h-full transition-all duration-500`}
                  style={{ width }}
                />
              </div>
              {i > 0 && s.dropoff_count > 0 && (
                <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
                  <TrendingDown className="w-3 h-3" />
                  Dropped {s.dropoff_count} · lost {fmtSAR(s.dropoff_amount)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between text-sm">
        <span className="text-gray-500">Leaked revenue (draft → converted)</span>
        <span className="font-bold text-red-600" data-testid="qa-leaked-revenue">
          {fmtSAR(funnel.leaked_revenue)}
        </span>
      </div>
    </div>
  );
};

// ─── Loss block ─────────────────────────────────────────────────
const LossBlock = ({ loss }) => {
  if (!loss) return null;
  return (
    <div
      data-testid="qa-loss-block"
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-5 h-5 text-amber-500" />
        <h3 className="text-lg font-bold text-gray-900">Loss Analysis</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Rejected + expired quotations · last {loss.window_days}d
      </p>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-red-50 rounded-lg p-3">
          <div className="text-xs text-red-700 mb-1">Total Lost</div>
          <div className="text-xl font-bold text-red-900" data-testid="qa-loss-total">
            {fmtSAR(loss.total_lost_value)}
          </div>
          <div className="text-xs text-red-600 mt-1">{loss.total_count} quotations</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-600 mb-1">Rejected</div>
          <div className="text-lg font-semibold text-gray-900">
            {fmtSAR(loss.rejected_value)}
          </div>
          <div className="text-xs text-gray-500 mt-1">{loss.rejected_count} quotations</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-600 mb-1">Expired</div>
          <div className="text-lg font-semibold text-gray-900">
            {fmtSAR(loss.expired_value)}
          </div>
          <div className="text-xs text-gray-500 mt-1">{loss.expired_count} quotations</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-700">Top loss-contributing customers</h4>
      </div>
      {loss.top_customers && loss.top_customers.length > 0 ? (
        <div className="border rounded-lg divide-y">
          {loss.top_customers.map((c) => (
            <div
              key={c.customer_id}
              data-testid={`qa-loss-customer-${c.customer_id}`}
              className="px-3 py-2 flex justify-between items-center text-sm"
            >
              <div>
                <div className="font-medium text-gray-900">{c.customer_name}</div>
                <div className="text-xs text-gray-500">
                  {c.rejected_count} rejected · {c.expired_count} expired
                </div>
              </div>
              <div className="font-bold text-red-600">{fmtSAR(c.lost_value)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-400 italic">No losses in window — clean pipeline.</div>
      )}
    </div>
  );
};

// ─── Time-to-convert block ──────────────────────────────────────
const TimingBlock = ({ timing }) => {
  if (!timing) return null;
  const cards = [
    { key: 'sent_to_accepted',      label: 'Sent → Accepted'      },
    { key: 'accepted_to_converted', label: 'Accepted → Converted' },
    { key: 'sent_to_converted',     label: 'Sent → Converted'     },
  ];
  return (
    <div
      data-testid="qa-timing-block"
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center gap-2 mb-1">
        <Clock className="w-5 h-5 text-blue-500" />
        <h3 className="text-lg font-bold text-gray-900">Time-to-Convert</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Median lag (days) between state-machine transitions · last {timing.window_days}d
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {cards.map(({ key, label }) => {
          const b = timing[key];
          const empty = !b || b.count === 0;
          return (
            <div
              key={key}
              data-testid={`qa-timing-${key}`}
              className={`rounded-lg p-4 border ${empty ? 'bg-gray-50 border-gray-200' : 'bg-blue-50 border-blue-200'}`}
            >
              <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                {label} <ArrowRight className="w-3 h-3" />
              </div>
              {empty ? (
                <div className="text-sm text-gray-400 italic">No data yet</div>
              ) : (
                <>
                  <div className="text-2xl font-bold text-blue-900">
                    {b.median_days}d
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    avg {b.avg_days}d · p90 {b.p90_days}d · n={b.count}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Discount intelligence block ────────────────────────────────
const DiscountBlock = ({ discount }) => {
  if (!discount) return null;
  return (
    <div
      data-testid="qa-discount-block"
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center gap-2 mb-1">
        <Percent className="w-5 h-5 text-emerald-600" />
        <h3 className="text-lg font-bold text-gray-900">Discount Intelligence</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Unit-price variance per item across quotations · last {discount.window_days}d
      </p>
      {discount.items && discount.items.length > 0 ? (
        <div className="space-y-4">
          {discount.items.map((item, idx) => (
            <div
              key={idx}
              data-testid={`qa-discount-item-${idx}`}
              className="border border-gray-200 rounded-lg p-3"
            >
              <div className="flex justify-between items-baseline mb-2">
                <div className="font-medium text-gray-900 truncate" title={item.description}>
                  {item.description}
                </div>
                <div className="text-xs text-gray-500">
                  median {fmtSAR(item.median_price)} · n={item.sample_size}
                </div>
              </div>
              <div className="space-y-1">
                {item.customers.map((c) => {
                  const isDiscount = c.delta_pct < 0;
                  const isPremium  = c.delta_pct > 0;
                  const cls = isDiscount
                    ? 'text-red-600 bg-red-50'
                    : isPremium
                    ? 'text-emerald-700 bg-emerald-50'
                    : 'text-gray-600 bg-gray-50';
                  return (
                    <div
                      key={c.customer_id}
                      className="flex justify-between items-center text-xs px-2 py-1 rounded"
                    >
                      <span className="text-gray-700">{c.customer_name}</span>
                      <span className="font-mono text-gray-500">{fmtSAR(c.avg_price)}</span>
                      <span className={`px-2 py-0.5 rounded font-semibold ${cls}`}>
                        {c.delta_pct > 0 ? '+' : ''}
                        {fmtPct(c.delta_pct)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-400 italic">
          Not enough data yet (need ≥ 3 line-items per description).
        </div>
      )}
    </div>
  );
};

// ─── Behavioral Intelligence blocks ─────────────────────────────

// Heatmap of patched fields. Wider bars = more friction in that field.
const FieldHeatmapBlock = ({ heatmap }) => {
  if (!heatmap) return null;
  const max = Math.max(...heatmap.items.map((it) => it.count), 1);
  return (
    <div
      data-testid="qa-behavior-heatmap"
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center gap-2 mb-1">
        <Flame className="w-5 h-5 text-rose-500" />
        <h3 className="text-lg font-bold text-gray-900">Edit Heatmap</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Which fields the negotiation touches most · last {heatmap.window_days}d
        · {heatmap.total_patches} patches total
      </p>
      {heatmap.items.length === 0 ? (
        <div className="text-sm text-gray-400 italic">
          No patches yet — edit a quotation to start collecting signals.
        </div>
      ) : (
        <div className="space-y-2">
          {heatmap.items.map((it, i) => (
            <div
              key={`${it.scope}-${it.field}-${i}`}
              data-testid={`qa-behavior-heat-row-${it.field}`}
              className="flex items-center gap-2 text-sm"
            >
              <div className="w-32 text-gray-700 truncate">
                <span className="text-xs text-gray-400 mr-1">{it.scope}.</span>
                <span className="font-medium">{it.field}</span>
              </div>
              <div className="flex-1 h-6 bg-gray-100 rounded-md overflow-hidden relative">
                <div
                  className="h-full bg-gradient-to-r from-rose-300 to-rose-500 transition-all duration-500"
                  style={{ width: `${Math.max((it.count / max) * 100, 4)}%` }}
                />
              </div>
              <div className="w-28 text-right">
                <span className="font-mono text-gray-700 text-xs">
                  {it.count} ({(it.pct * 100).toFixed(0)}%)
                </span>
                {it.direction && (
                  <span className={`ml-1 text-xs ${it.direction === 'down' ? 'text-red-600' : 'text-emerald-600'}`}>
                    {it.direction === 'down' ? '↓' : '↑'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const REASON_META = {
  price_objection:        { label: 'Price Objection',        cls: 'bg-red-50 text-red-700 border-red-200' },
  quantity_negotiation:   { label: 'Quantity Negotiation',   cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  scope_change:           { label: 'Scope Change',           cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  contract_clarification: { label: 'Contract Clarification', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  mixed_negotiation:      { label: 'Mixed Negotiation',      cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  unspecified:            { label: 'Unspecified',            cls: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const ReasonBreakdownBlock = ({ reasons }) => {
  if (!reasons) return null;
  const max = Math.max(...reasons.rows.map((r) => r.count), 1);
  return (
    <div
      data-testid="qa-behavior-reasons"
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-5 h-5 text-indigo-500" />
        <h3 className="text-lg font-bold text-gray-900">Revision Reason Breakdown</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Why revisions happen · {reasons.total_revisions_analysed} revisions analysed · last {reasons.window_days}d
      </p>
      <div className="space-y-2">
        {reasons.rows.map((row) => {
          const meta = REASON_META[row.label] || REASON_META.unspecified;
          return (
            <div
              key={row.label}
              data-testid={`qa-behavior-reason-${row.label}`}
              className={`flex items-center gap-2 text-sm border rounded-md px-3 py-2 ${meta.cls}`}
            >
              <span className="font-medium flex-1">{meta.label}</span>
              <div className="w-32 h-2 bg-white/70 rounded-full overflow-hidden">
                <div
                  className="h-full bg-current opacity-70"
                  style={{ width: `${Math.max((row.count / max) * 100, 2)}%` }}
                />
              </div>
              <span className="w-20 text-right font-mono text-xs">
                {row.count} ({(row.pct * 100).toFixed(0)}%)
              </span>
              <span className="text-xs opacity-70 w-16 text-right">
                conf {(row.avg_confidence * 100).toFixed(0)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const NegotiationProfileBlock = ({ customers }) => {
  if (!customers) return null;
  return (
    <div
      data-testid="qa-behavior-customers"
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2"
    >
      <div className="flex items-center gap-2 mb-1">
        <Brain className="w-5 h-5 text-emerald-600" />
        <h3 className="text-lg font-bold text-gray-900">Client Negotiation Profile</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Aggressiveness score = revisions × discount pressure × low conversion · last {customers.window_days}d
      </p>
      {customers.rows.length === 0 ? (
        <div className="text-sm text-gray-400 italic">No customer activity yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 px-2">Customer</th>
                <th className="text-right py-2 px-2">Chains</th>
                <th className="text-right py-2 px-2">Avg Revisions</th>
                <th className="text-right py-2 px-2">Avg Concession %</th>
                <th className="text-right py-2 px-2">Conversion</th>
                <th className="text-right py-2 px-2">Aggressiveness</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {customers.rows.map((row) => {
                const score = row.aggressiveness_score;
                const tone = score >= 60 ? 'text-red-600' : score >= 30 ? 'text-amber-600' : 'text-emerald-600';
                return (
                  <tr
                    key={row.customer_id}
                    data-testid={`qa-behavior-customer-${row.customer_id}`}
                  >
                    <td className="py-2 px-2 font-medium text-gray-900">{row.customer_name}</td>
                    <td className="py-2 px-2 text-right font-mono">{row.n_chains}</td>
                    <td className="py-2 px-2 text-right font-mono">{row.avg_revisions}</td>
                    <td className={`py-2 px-2 text-right font-mono ${row.avg_concession_pct < 0 ? 'text-red-600' : ''}`}>
                      {(row.avg_concession_pct * 100).toFixed(1)}%
                    </td>
                    <td className="py-2 px-2 text-right font-mono">
                      {(row.conversion_rate * 100).toFixed(0)}%
                    </td>
                    <td className={`py-2 px-2 text-right font-bold ${tone}`}>
                      {score}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Page shell ─────────────────────────────────────────────────
const QuotationAnalytics = () => {
  const [windowDays, setWindowDays] = useState(90);
  const [loading, setLoading]   = useState(true);
  const [funnel, setFunnel]     = useState(null);
  const [loss, setLoss]         = useState(null);
  const [timing, setTiming]     = useState(null);
  const [discount, setDiscount] = useState(null);
  const [heatmap, setHeatmap]   = useState(null);
  const [reasons, setReasons]   = useState(null);
  const [customers, setCustomers] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics');   // 'analytics' | 'behavior'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [f, l, t, d, h, r, c] = await Promise.all([
          axiosInstance.get(`/quotation-analytics/funnel?days=${windowDays}`),
          axiosInstance.get(`/quotation-analytics/loss-analysis?days=${windowDays}&top_n=5`),
          axiosInstance.get(`/quotation-analytics/time-to-convert?days=${windowDays}`),
          axiosInstance.get(`/quotation-analytics/discount-intelligence?days=${Math.max(windowDays, 90)}`),
          axiosInstance.get(`/quotation-behavior/heatmap?days=${windowDays}`),
          axiosInstance.get(`/quotation-behavior/reasons?days=${windowDays}`),
          axiosInstance.get(`/quotation-behavior/customers?days=${Math.max(windowDays, 180)}&top_n=10`),
        ]);
        if (cancelled) return;
        setFunnel(f.data);
        setLoss(l.data);
        setTiming(t.data);
        setDiscount(d.data);
        setHeatmap(h.data);
        setReasons(r.data);
        setCustomers(c.data);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load quotation analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [windowDays]);

  return (
    <div data-testid="quotation-analytics-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Quotation Analytics</h2>
          <p className="text-sm text-gray-500">
            Operational intelligence on the pre-invoice pipeline.
          </p>
        </div>
        <div
          data-testid="qa-window-selector"
          className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden"
        >
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              data-testid={`qa-window-${opt.value}`}
              onClick={() => setWindowDays(opt.value)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                windowDays === opt.value
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab switcher — Analytics vs Behavioral Intelligence */}
      <div
        data-testid="qa-tab-switcher"
        className="border-b border-gray-200 flex gap-1"
      >
        {[
          { id: 'analytics', label: 'Operational Analytics' },
          { id: 'behavior',  label: 'Behavioral Intelligence' },
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`qa-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading analytics…</div>
      ) : activeTab === 'analytics' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FunnelBlock funnel={funnel} />
          <LossBlock   loss={loss} />
          <TimingBlock timing={timing} />
          <DiscountBlock discount={discount} />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FieldHeatmapBlock heatmap={heatmap} />
          <ReasonBreakdownBlock reasons={reasons} />
          <NegotiationProfileBlock customers={customers} />
        </div>
      )}
    </div>
  );
};

export default QuotationAnalytics;
